import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/ui/loading";
import { useEffect } from "react";
import { axiosInstance } from "@/pages/auth/auth.context";

const formSchema = z.object({
  contactName: z.string().min(2, "Contact name must be at least 2 characters"),
  companyName: z.string().min(2, "Company name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phoneNumber: z.string().min(10, "Phone number must be at least 10 digits"),
});

type FormValues = z.infer<typeof formSchema>;

interface Client {
  contactName: string;
  companyName: string;
  email: string;
  phoneNumber: string;
}

export function ClientForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      contactName: "",
      companyName: "",
      email: "",
      phoneNumber: "",
    },
  });

  const { data: clientData, isLoading: isLoadingClient } = useQuery<Client>({
    queryKey: ["client", id],
    queryFn: async () => {
      if (!id) return null;
      const response = await axiosInstance.get<Client>(`client/${id}`);
      return response.data;
    },
    enabled: isEditMode, // Other options like `enabled` go here
  });

  useEffect(() => {
    if (clientData) {
      form.reset(clientData);
    }
  }, [clientData, form]);

  const createMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      const response = await axiosInstance.post(`client`, data);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Client created successfully!",
        variant: "success",
      });
      navigate("/admin/clients");
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create client",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      const response = await axiosInstance.put(`client/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Client updated successfully!",
        variant: "success",
      });
      navigate("/admin/clients");
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update client",
        variant: "destructive",
      });
    },
  });

  async function onSubmit(values: FormValues) {
    if (isEditMode) {
      updateMutation.mutate(values);
    } else {
      createMutation.mutate(values);
    }
  }

  if (isEditMode && isLoadingClient) {
    return (
      <>
        <Loading />
      </>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="contactName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Contact Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 555-5555" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-4">
          <Button type="submit">
            {isEditMode ? "Update Client" : "Create Client"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/clients")}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
